import usePeriodLevels from "backend/hooks/model/usePeriodLevels";
import { IPeriod, IPeriodLevel } from "backend/model/model";
import PeriodSelectorDesign from "design/components/toolbar/PeriodSelectorDesign";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import useLink from "qpr-base/hooks/useLink";
import React from "react";

const PeriodSelector = (props: { level?: string }) => {
  const { data: periodlevels } = usePeriodLevels();
  const link = useLink();
  const ctx = useAppContext();
  const [activePeriod, setActivePeriod] = React.useState<IPeriod | undefined>();
  const [activePeriodLevel, setActivePeriodLevel] = React.useState<
    IPeriodLevel | undefined
  >();

  React.useEffect(() => {
    // if first time this is used
    if (periodlevels && !activePeriodLevel && ctx.period) {
      periodlevels.forEach((level) => {
        level.periods.forEach((period) => {
          if (period.name === ctx.period) {
            setActivePeriod(period);
            setActivePeriodLevel(level);
          }
        });
      });
    }
  }, [periodlevels, activePeriod?.name, ctx.period]);

  const usedPeriodLevels = React.useMemo(() => {
    let result = periodlevels;
    if (props.level)
      result = periodlevels?.filter((l) => l.name === props.level);
    if (!result || result.length === 0) return [];
    return result;
  }, [props.level, periodlevels]);

  if (!periodlevels || !usedPeriodLevels) return null;

  const isShorterPeriodLevel = (newlevel: IPeriodLevel) => {
    const levelIndex = (level: IPeriodLevel) => {
      for (let i = 0; i < usedPeriodLevels.length; i += 1)
        if (usedPeriodLevels[i].id === level.id) return i;
      return 0;
    };
    if (!activePeriodLevel) return 0;
    return levelIndex(newlevel) < levelIndex(activePeriodLevel);
  };

  const getMatchingShorterPeriod = (
    newlevel: IPeriodLevel
  ): IPeriod | undefined => {
    if (!activePeriod) return undefined;

    for (let i = 0; i < newlevel.periods.length; i += 1) {
      if (newlevel.periods[i].enddate === activePeriod.enddate)
        return newlevel.periods[i];
    }
    return undefined;
  };

  const getMatchingLongerPeriod = (
    newlevel: IPeriodLevel
  ): IPeriod | undefined => {
    if (!activePeriod) return undefined;
    // return period with same enddate if such exists
    for (let i = 0; i < newlevel.periods.length; i += 1)
      if (newlevel.periods[i].enddate === activePeriod.enddate)
        return newlevel.periods[i];
    const currentStartDate = new Date(activePeriod.startdate);
    // return last ended period before current period
    for (let i = 0; i < newlevel.periods.length; i += 1) {
      const periodEndDate = new Date(newlevel.periods[i].enddate);
      if (periodEndDate > currentStartDate && i > 0)
        return newlevel.periods[i - 1];
    }
    return undefined;
  };

  return (
    <div>
      <PeriodSelectorDesign
        periodlevels={usedPeriodLevels}
        selectedLevel={activePeriodLevel}
        selectedPeriod={activePeriod}
        onSelectPeriod={(value) => {
          periodlevels.forEach((l) => {
            l.periods.forEach((period) => {
              if (period.id === value) {
                setActivePeriod(period);
                link({ period: period.name });
              }
            });
          });
        }}
        onSelectLevel={(value) => {
          const level = periodlevels.filter((pl) => pl.id === value).pop();
          if (!level?.periods?.length) return;
          let period;
          if (isShorterPeriodLevel(level))
            period = getMatchingShorterPeriod(level);
          else period = getMatchingLongerPeriod(level);
          setActivePeriodLevel(level);
          setActivePeriod(period);
          if (period) link({ period: period.name });
        }}
      />
    </div>
  );
};

export default PeriodSelector;
